import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import { PriceDisplay } from "@b2bportal/core-ui";
import {
  getPriceFreezeExpiryDate,
  isPriceFreezeVoucherActive,
} from "@b2bportal/core-utilities";
import {
  type FrozenProductFlight,
  StateEnum,
  type PassengerCount,
} from "@b2bportal/price-freeze-api";
import { DateTimeFormatStyle, useI18nContext } from "@hopper-b2b/i18n";
import { noop, removeTimezone } from "@hopper-b2b/utilities";
import dayjs from "dayjs";
import type { FrozenFlightCardProps } from "./FrozenFlightCard";

const DATE_FORMAT = DateTimeFormatStyle.ShortMonthDayShortWeekday;
const SHORT_TIME_FORMAT = DateTimeFormatStyle.ShortTime;

export const useFrozenFlightCard = (props: FrozenFlightCardProps) => {
  const { priceFreezeVoucher: voucher } = props;
  const navigate = useNavigate();
  const { t, formatDateTime, formatFiatCurrency } = useI18nContext();
  const frozenFlight = voucher.frozenProduct as FrozenProductFlight;
  const origin =
    frozenFlight.tripDetails?.slices[0]?.originCode ??
    frozenFlight.segments[0].origin;
  const departureDate =
    frozenFlight.tripDetails?.slices[0]?.zonedDepartureTime ??
    frozenFlight.segments[0].departureTime;
  const destination =
    frozenFlight.tripDetails?.slices[0]?.destinationCode ??
    frozenFlight.segments[0].destination;
  const returnDate =
    frozenFlight.tripDetails && frozenFlight.tripDetails?.slices?.length > 1
      ? frozenFlight.tripDetails.slices[1].departureTime
      : null;

  const originLabel = frozenFlight.tripDetails?.slices[0]?.originName ?? origin;
  const destinationLabel =
    frozenFlight.tripDetails?.slices[0]?.destinationName ?? destination;

  const travelerPrice = formatFiatCurrency(
    frozenFlight.quotedPrice?.fiat ?? frozenFlight.shoppedPrice.fiat
  );

  const expiration = getPriceFreezeExpiryDate(voucher);

  const isActive = isPriceFreezeVoucherActive(voucher);

  const expirationString = useMemo(() => {
    if (isActive) {
      return t("priceFreeze.frozenPriceExpirationString", {
        date: formatDateTime(dayjs(expiration).toDate(), DATE_FORMAT),
      });
    }

    if (voucher.voucherState.State === StateEnum.Refunded) {
      return t("priceFreeze.frozenPriceRefundedString", {
        date: formatDateTime(dayjs(expiration).toDate(), DATE_FORMAT),
      });
    }

    if (voucher.voucherState.State === StateEnum.Exercised) {
      return t("priceFreeze.frozenPriceBookedString", {
        date: formatDateTime(dayjs(expiration).toDate(), DATE_FORMAT),
      });
    }

    if (voucher.voucherState.State === StateEnum.Cancelled) {
      return t("priceFreeze.frozenPriceCancelledString", {
        date: formatDateTime(dayjs(expiration).toDate(), DATE_FORMAT),
      });
    }

    return t("priceFreeze.frozenPriceExpiredString", {
      date: formatDateTime(dayjs(expiration).toDate(), DATE_FORMAT),
    });
  }, [isActive, voucher.voucherState.State, t, formatDateTime, expiration]);

  const onBook = useCallback(
    () => navigate(`${encodeURIComponent(voucher.priceFreezeId)}`),
    [navigate, voucher.priceFreezeId]
  );

  const getFrozenFlightCardTitle = useCallback(() => {
    return t("originToDestination", {
      origin: `${originLabel} (${origin})`,
      destination: `${destinationLabel} (${destination})`,
      interpolation: { escapeValue: false },
    });
  }, [destination, destinationLabel, origin, originLabel, t]);

  const frozenFlightPassengerCount = useMemo(() => {
    const { passengerCount } = frozenFlight;

    return (Object.keys(passengerCount) as (keyof PassengerCount)[]).reduce(
      (acc, key) => {
        if (key !== "numOfLapInfants") {
          return acc + passengerCount[key];
        }
        return acc;
      },
      0
    );
  }, [frozenFlight]);

  return useMemo(() => {
    return {
      text: {
        bookNow: t("priceFreeze.priceFreezeBookButton"),
        title: getFrozenFlightCardTitle(),
        departureDate: formatDateTime(
          dayjs(departureDate).toDate(),
          DATE_FORMAT
        ),
        returnDate:
          returnDate !== null && returnDate !== undefined
            ? formatDateTime(dayjs(returnDate).toDate(), DATE_FORMAT)
            : "",
        departureTime: formatDateTime(
          dayjs(removeTimezone(departureDate)).toDate(),
          SHORT_TIME_FORMAT
        ),
        returnTime:
          returnDate !== null && returnDate !== undefined
            ? formatDateTime(
                dayjs(removeTimezone(returnDate)).toDate(),
                SHORT_TIME_FORMAT
              )
            : "",
        tripType:
          returnDate !== null && returnDate !== undefined
            ? t("flightRoundTrip")
            : t("flightOneWay"),
        priceString: t("priceFreeze.frozenPriceString", {
          price: travelerPrice,
        }),
        expirationString,
      },
      elements: {
        activePriceElement: isActive ? (
          <PriceDisplay
            prices={frozenFlight.quotedPrice ?? frozenFlight.shoppedPrice}
          />
        ) : null,
      },
      context: {
        isActive,
        passengerCount: frozenFlightPassengerCount,
      },
      handlers: {
        onBook: isActive ? onBook : noop,
      },
    };
  }, [
    t,
    getFrozenFlightCardTitle,
    formatDateTime,
    departureDate,
    returnDate,
    travelerPrice,
    expirationString,
    isActive,
    frozenFlight.quotedPrice,
    frozenFlight.shoppedPrice,
    frozenFlightPassengerCount,
    onBook,
  ]);
};
